import React from 'react';
import { motion } from 'framer-motion';
import { Waves, Heart, Award, MapPin } from 'lucide-react';

export default function AboutSection() {
  const features = [
    {
      icon: Waves,
      title: 'Perfect Waves',
      description: 'Essaouira offers consistent Atlantic swells perfect for all levels'
    },
    {
      icon: Heart,
      title: 'Local Passion',
      description: 'Born from the love of surf culture and Moroccan hospitality'
    },
    {
      icon: Award,
      title: 'Expert Instructors',
      description: 'ISA-certified coaches with years of local surf knowledge'
    },
    {
      icon: MapPin,
      title: 'Prime Location',
      description: 'Steps from the best surf spots on the Essaouira coast'
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-blue-50/30 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block mb-4">
              <span className="px-4 py-2 rounded-full bg-cyan-100 text-cyan-700 text-sm font-semibold">
                Our Story
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Where Ocean Meets
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-cyan-500">  Mogador's Culture</span>
            </h2>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Founded by local surfers who grew up riding the legendary waves of Essaouira, 
              our shop embodies the spirit of Morocco's Atlantic coast. We're more than a surf shop—we're 
              a community hub where travelers become friends and waves become memories.
            </p>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Essaouira's unique combination of steady trade winds, consistent swells, and warm culture 
              creates the perfect environment for learning and progressing. Our mission is to share this 
              magic with surfers from around the world.
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-2 gap-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex flex-col gap-2"
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white mb-2">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-bold text-gray-900">{feature.title}</h3>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Right Column - Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src="https://exploreessaouira.com/wp-content/uploads/2022/02/Essaouira-FB-cover.jpg"
                    alt="Surfing in Essaouira"
                    className="w-full h-64 object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src="https://static.nationalgeographic.fr/files/styles/image_3200/public/weekendergettyimagesessouiraresizedlead.jpg?w=1900&h=1267"
                    alt="Surf shop interior"
                    className="w-full h-48 object-cover"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src="https://www.lovingsurf.com/wp-content/uploads/2019/06/D3S_1707.jpg"
                    alt="Essaouira beach"
                    className="w-full h-48 object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src="https://media.tacdn.com/media/attractions-splice-spp-674x446/07/ba/03/d6.jpg"
                    alt="Surf coaching"
                    className="w-full h-64 object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Floating Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-2xl p-6 border-4 border-blue-50"
            >
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-1">15+</div>
                <div className="text-sm text-gray-600 font-medium">Years Experience</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}